import { Link } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useNavigate, useLocation, Navigate, Outlet, Link as RouterLink } from 'react-router-dom'

import IconButton from '@mui/material/IconButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Button from '@mui/material/Button'
import LogoutIcon from '@mui/icons-material/Logout'
import ProfileMenuItem from '../../components/ui/ProfileItemMenu'
import UserAvatar from '../../components/ui/UserAvatar'
import ErrorPage from '../../components/errors/ErrorPage'

import { useLogin, useLogout, useMe } from '../api/auth'
import { useQueryClient } from 'react-query'

export const UserAuthContext = React.createContext({
  user: null,
  isAuthorized: false,
  login: async (credentials) => {},
  logout: async () => {},
  error: null,
})

export function UserAuthProvider({ children }) {
  const navigate = useNavigate()
  const location = useLocation()

  const queryClient = useQueryClient()

  const { firstRun, user, setUser, error, fetchUser, clearUser } = useMe()

  const { mutateAsync: doLogin } = useLogin()
  const { mutateAsync: doLogout } = useLogout()

  const login = async (data) => {
    try {
      const res = await doLogin(data)
      await fetchUser()
    } catch (err) {
      if (err.response?.data) {
        return err.response?.data
      } else {
        console.error(err)
      }
    }
  }

  const logout = async (data) => {
    try {
      await doLogout()
    } catch (err) {
      console.error(err)
    } finally {
      clearUser()
    }
  }

  useEffect(() => {
    const state = { ...(location?.state ?? {}) }
    const from = state.from

    if (user && from) {
      delete state.from
      navigate(from, { replace: true, state })
      return
    }
    if (!firstRun && user === null) {
      queryClient
        .cancelQueries()
        .then(() => queryClient.removeQueries())
        .finally(() => navigate('/', { state: {} }))
    }
    // eslint-disable-next-line
  }, [user])

  let value = {
    user,
    login,
    logout,
    canRegister: Boolean(user?.dealershipName),
    error,
    fetchUser,
  }

  return (
    <UserAuthContext.Provider value={value}>{firstRun ? null : children}</UserAuthContext.Provider>
  )
}

export function useAuth() {
  return React.useContext(UserAuthContext)
}

export function UserAuthStatus() {
  const auth = useAuth()
  const location = useLocation()
  const [accountAnchorEl, setAccountAnchorEl] = useState(null)

  const handleAccountMenu = (event) => {
    setAccountAnchorEl(event.currentTarget)
  }

  const handleAccountMenuClose = () => {
    setAccountAnchorEl(null)
  }

  const handleLogout = () => {
    handleAccountMenuClose()
    auth.logout()
  }

  const handleProfile = () => {}

  if (!auth.user) {
    if (['/mercedes-benz', '/bmw', '/jlr', '/vw', '/byd', 'vwsg'].includes(location.pathname)) {
      return null
    }
    return (
      <div>
        <Button component={RouterLink} to='/login' color='primary'>
          Dealer Login
        </Button>
      </div>
    )
  }

  return (
    <div>
      {/* {!auth.isAuthorized && (
        <Button component={RouterLink} to='/enquiry' color='primary'>
          Enquiry
        </Button>
      )} */}
      <IconButton
        aria-label='account'
        aria-controls='menu-account'
        aria-haspopup='true'
        aria-expanded={Boolean(accountAnchorEl)}
        onClick={handleAccountMenu}
        color='inherit'
      >
        <UserAvatar size={32} user={auth.user} />
      </IconButton>
      <Menu
        id='menu-account'
        anchorEl={accountAnchorEl}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        keepMounted
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={Boolean(accountAnchorEl)}
        onClose={handleAccountMenuClose}
      >
        <ProfileMenuItem onClick={handleProfile} />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </Menu>
    </div>
  )
}

export function RequireAuth() {
  let auth = useAuth()
  let location = useLocation()

  if (!auth.user) {
    return <Navigate to='/login' replace state={{ from: location }} />
  }

  return <Outlet />
}

export function RequireAdmin() {
  let auth = useAuth()

  if (!auth.user?.isAdmin) {
    return (
      <ErrorPage error={{ statusText: 'Access Denied' }}>
        <p>You don't have the necessary privileges to access this page.</p>
        <p>
          If you believe that this is a mistake, please contact our IT team:{' '}
          <Link component='a' href='mailto:technology@htgroup.com.my'>
            technology@htgroup.com.my
          </Link>{' '}
        </p>
      </ErrorPage>
    )
  }

  return <Outlet />
}

export function RequireRole({ roles }) {
  let auth = useAuth()

  if (!roles.includes(auth.user?.role)) {
    return (
      <ErrorPage error={{ statusText: 'Access Denied' }}>
        <p>You don't have the necessary privileges to access this page.</p>
        <p>
          If you believe that this is a mistake, please contact our IT team:{' '}
          <Link component='a' href='mailto:technology@htgroup.com.my'>
            technology@htgroup.com.my
          </Link>{' '}
        </p>
      </ErrorPage>
    )
  }

  return <Outlet />
}

export function RequireDealershipBrand({ dealershipBrands }) {
  let auth = useAuth()

  if (auth.user && !dealershipBrands.includes(auth.user?.dealershipBrand)) {
    return (
      <ErrorPage error={{ statusText: 'Access Denied' }}>
        <p>You don't have the necessary privileges to access this page.</p>
        <p>
          If you believe that this is a mistake, please contact our IT team:{' '}
          <Link component='a' href='mailto:technology@htgroup.com.my'>
            technology@htgroup.com.my
          </Link>{' '}
        </p>
      </ErrorPage>
    )
  }

  return <Outlet />
}
